import React, { useCallback, useMemo } from "react";
import { AccountsIcon, SourceIcon, StatusIcon } from "@icons";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import {
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import overlayStyles from "@components/pageHeader/filters/FiltersOverlay.module.scss";
import {
    INTERCOMPANY_TRANSACTION_SOURCE_LABELS,
    IntercompanyTransactionSource,
    IntercompanyTransactionStatus,
} from "./intercompanyTransactions.lib";
import { INTERCOMPANY_GL_ACCOUNT_LABEL } from "./intercompanyTransactionsViewConfig";
import {
    ACCOUNT_TAB,
    IntercompanyFilterTab,
    SOURCE_OPTIONS,
    SOURCE_TAB,
    STATUS_OPTIONS,
    STATUS_TAB,
    isAccountId,
    isSource,
    isStatus,
} from "./intercompanyTransactionsFilters.lib";
import {
    IntercompanyActiveFilterChip,
    IntercompanyFilterNavTab,
    IntercompanyFilterPanel,
} from "./IntercompanyTransactionsFilterParts";

interface IntercompanyTransactionsFiltersProps {
    statuses: IntercompanyTransactionStatus[];
    accountIds: string[];
    sources: IntercompanyTransactionSource[];
    accounts: Array<{ id: string; label: string }>;
    onStatusesChange: (statuses: IntercompanyTransactionStatus[]) => void;
    onAccountIdsChange: (accountIds: string[]) => void;
    onSourcesChange: (sources: IntercompanyTransactionSource[]) => void;
}

export const IntercompanyTransactionsFilters = ({
    statuses,
    accountIds,
    sources,
    accounts,
    onStatusesChange,
    onAccountIdsChange,
    onSourcesChange,
}: IntercompanyTransactionsFiltersProps): React.ReactElement => {
    const accountOptions: FilterMultiselectOption[] = useMemo(
        () =>
            accounts.map((account) => ({
                value: account.id,
                label: account.label,
            })),
        [accounts],
    );
    const activeFilters = useMemo<IntercompanyFilterTab[]>(() => {
        const active: IntercompanyFilterTab[] = [];
        if (statuses.length > 0) {
            active.push(STATUS_TAB);
        }
        if (accountIds.length > 0) {
            active.push(ACCOUNT_TAB);
        }
        if (sources.length > 0) {
            active.push(SOURCE_TAB);
        }
        return active;
    }, [accountIds.length, sources.length, statuses.length]);

    const handleStatusValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onStatusesChange(values.filter(isStatus)),
        [onStatusesChange],
    );
    const handleAccountValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onAccountIdsChange(values.filter(isAccountId)),
        [onAccountIdsChange],
    );
    const handleSourceValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onSourcesChange(values.filter(isSource)),
        [onSourcesChange],
    );
    const accountLabels = useMemo(
        () =>
            accountIds.map(
                (accountId) =>
                    accounts.find((account) => account.id === accountId)
                        ?.label ?? accountId,
            ),
        [accountIds, accounts],
    );
    const sourceLabels = useMemo(
        () =>
            sources.map(
                (source) => INTERCOMPANY_TRANSACTION_SOURCE_LABELS[source],
            ),
        [sources],
    );
    const resetStatuses = useCallback(
        () => handleStatusValuesChange([]),
        [handleStatusValuesChange],
    );
    const resetAccountIds = useCallback(
        () => handleAccountValuesChange([]),
        [handleAccountValuesChange],
    );
    const resetSources = useCallback(
        () => handleSourceValuesChange([]),
        [handleSourceValuesChange],
    );
    const clearFilters = useCallback(() => {
        onStatusesChange([]);
        onAccountIdsChange([]);
        onSourcesChange([]);
    }, [onAccountIdsChange, onSourcesChange, onStatusesChange]);
    const renderActiveFilterChip = useCallback(
        (tabKey: IntercompanyFilterTab) => {
            switch (tabKey) {
                case STATUS_TAB:
                    return (
                        <IntercompanyActiveFilterChip
                            key={tabKey}
                            tabKey={STATUS_TAB}
                            pluralNoun="Statuses"
                            labels={statuses}
                            onReset={resetStatuses}
                        />
                    );
                case ACCOUNT_TAB:
                    return (
                        <IntercompanyActiveFilterChip
                            key={tabKey}
                            tabKey={ACCOUNT_TAB}
                            pluralNoun="Accounts"
                            labels={accountLabels}
                            onReset={resetAccountIds}
                        />
                    );
                case SOURCE_TAB:
                    return (
                        <IntercompanyActiveFilterChip
                            key={tabKey}
                            tabKey={SOURCE_TAB}
                            pluralNoun="Sources"
                            labels={sourceLabels}
                            onReset={resetSources}
                        />
                    );
                default: {
                    const exhaustiveCheck: never = tabKey;
                    return exhaustiveCheck;
                }
            }
        },
        [
            accountLabels,
            resetAccountIds,
            resetSources,
            resetStatuses,
            sourceLabels,
            statuses,
        ],
    );

    return (
        <AggregatedFiltersProvider defaultTab={STATUS_TAB}>
            <AggregatedFilters label="Add filter">
                <AggregatedFiltersOverlay
                    filterClassname={overlayStyles.filter}
                    navigation={
                        <>
                            <IntercompanyFilterNavTab
                                tabKey={STATUS_TAB}
                                label="Status"
                                icon={<StatusIcon />}
                            />
                            <IntercompanyFilterNavTab
                                tabKey={ACCOUNT_TAB}
                                label={INTERCOMPANY_GL_ACCOUNT_LABEL}
                                icon={<AccountsIcon />}
                            />
                            <IntercompanyFilterNavTab
                                tabKey={SOURCE_TAB}
                                label="Source"
                                icon={<SourceIcon />}
                            />
                        </>
                    }
                    content={
                        <>
                            <IntercompanyFilterPanel
                                tabKey={STATUS_TAB}
                                name="Status"
                                options={STATUS_OPTIONS}
                                value={statuses}
                                onChange={handleStatusValuesChange}
                            />
                            <IntercompanyFilterPanel
                                tabKey={ACCOUNT_TAB}
                                name={INTERCOMPANY_GL_ACCOUNT_LABEL}
                                options={accountOptions}
                                value={accountIds}
                                onChange={handleAccountValuesChange}
                                isSearchable
                            />
                            <IntercompanyFilterPanel
                                tabKey={SOURCE_TAB}
                                name="Source"
                                options={SOURCE_OPTIONS}
                                value={sources}
                                onChange={handleSourceValuesChange}
                            />
                        </>
                    }
                />
            </AggregatedFilters>

            {activeFilters.length > 0 && (
                <ActiveFilters
                    activeFilters={activeFilters}
                    maxVisibleFilters={activeFilters.length}
                    clearFilters={clearFilters}
                    renderVisibleFilters={(visibleFilters) =>
                        visibleFilters.map(renderActiveFilterChip)
                    }
                />
            )}
        </AggregatedFiltersProvider>
    );
};
